// Buffer polyfill - must be first!
import { Buffer } from 'buffer';
if (typeof window !== 'undefined') {
  (window as any).Buffer = Buffer;
  (window as any).global = window;
  (window as any).process = { env: {} };
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import DebugPanel, { addLog } from './components/DebugPanel';

console.log('[main.tsx] Module loading...');

// Helper to update loading status
function updateLoadingStatus(message: string) {
  console.log('[main.tsx] ' + message);
  const status = document.getElementById('loading-status');
  if (status) status.textContent = message;
}

// Hide loading screen
function hideLoadingScreen() {
  const loading = document.getElementById('loading');
  if (loading) loading.classList.add('hidden');
}

// Override console to capture logs
const originalConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
};

console.log = (...args) => {
  addLog(args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' '));
  originalConsole.log(...args);
};

console.error = (...args) => {
  addLog('❌ ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' '));
  originalConsole.error(...args);
};

console.warn = (...args) => {
  addLog('⚠️ ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' '));
  originalConsole.warn(...args);
};

// Error Boundary
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      hideLoadingScreen();
      return React.createElement('div', {
        style: { padding: '20px', textAlign: 'center', background: '#1a1a1a', minHeight: '100vh', color: 'white' }
      }, [
        React.createElement('h2', { key: 'h2' }, 'Algo salió mal'),
        React.createElement('p', { key: 'msg', style: { color: '#ff6b6b', fontSize: '14px', marginTop: '10px' } }, this.state.error?.message || 'Error desconocido'),
        React.createElement('pre', { key: 'stack', style: { color: '#888', fontSize: '12px', marginTop: '10px', textAlign: 'left', overflow: 'auto' } }, this.state.error?.stack || ''),
        React.createElement('button', { key: 'btn', onClick: () => window.location.reload(), style: { marginTop: '20px', padding: '10px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' } }, 'Recargar'),
      ]);
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60 * 5, retry: 1 },
  },
});

// Manifest URL for TON Connect
const manifestUrl = import.meta.env.PROD
  ? 'https://crowdbuy-twa.pages.dev/tonconnect-manifest.json'
  : '/tonconnect-manifest.json';

// Main render function
async function renderApp() {
  try {
    console.log('[main.tsx] Starting render...');
    updateLoadingStatus('Cargando estilos...');

    // Import CSS
    await import('./index.css');

    updateLoadingStatus('Cargando Telegram SDK...');
    await import('./utils/telegram');

    updateLoadingStatus('Cargando componentes...');
    const { default: App } = await import('./App');

    updateLoadingStatus('Inicializando React...');

    const rootElement = document.getElementById('root');
    if (!rootElement) throw new Error('Root element not found');

    const app = React.createElement(
      React.StrictMode,
      null,
      React.createElement(
        ErrorBoundary,
        null,
        React.createElement(
          TonConnectUIProvider,
          { manifestUrl, children:
            React.createElement(
              QueryClientProvider,
              { client: queryClient },
              React.createElement(
                BrowserRouter,
                null,
                React.createElement(App)
              )
            )
          }
        ),
        React.createElement(DebugPanel)
      )
    );

    ReactDOM.createRoot(rootElement).render(app);

    console.log('[main.tsx] React rendered successfully');
    updateLoadingStatus('Cargando TON Connect...');

    setTimeout(() => {
      hideLoadingScreen();
      console.log('[main.tsx] App ready');
    }, 100);

  } catch (error) {
    console.error('[main.tsx] Render error:', error);
    const loading = document.getElementById('loading');
    if (loading) {
      loading.innerHTML = `
        <div style="text-align:center;padding:20px;">
          <h2 style="color:#ff6b6b;margin-bottom:10px;">Error</h2>
          <p style="color:#fff;font-size:14px;">${error instanceof Error ? error.message : 'Error desconocido'}</p>
          <pre style="color:#888;font-size:11px;margin-top:10px;">${error instanceof Error ? error.stack : ''}</pre>
          <button onclick="location.reload()" style="margin-top:20px;padding:10px 20px;background:#007bff;color:white;border:none;border-radius:5px;cursor:pointer;">Recargar</button>
        </div>
      `;
    }
  }
}

// Start app
console.log('[main.tsx] Script executed');
updateLoadingStatus('Iniciando aplicación...');
renderApp();